import { useCallback, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { useAiActionsModal } from "@/react/components/SettingsApp/Plan/AiActionsModal";
import type { Action } from "@/react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { workflowsApi } from "../api";
import {
  MODAL_RESULT,
  useDisabledAgentsModal,
} from "../components/SettingsApp/Workflows/components/DisabledAgentsInWorkflowsModal";
import {
  enableAgents,
  getAgentEnabledStatusInActions,
} from "../components/SettingsApp/Workflows/components/DisabledAgentsInWorkflowsModal/useAgentEnabledStatusInActions";
import { useShouldShowAiModal } from "./useShouldShowAiModal";

interface UseAiWorkflowActivationModalOptions {
  actions?: Action[];
  steps?: Action["action_type"][];
  workflowId?: string;
}

// Utility function to check if workflow has AI-related actions
const hasAiActions = (
  actions?: Action[],
  steps?: Action["action_type"][],
): boolean => {
  if (actions?.length) {
    return actions.some(action => action.action_type?.startsWith("ai_"));
  }
  if (steps?.length) {
    return steps.some(step => step?.startsWith("ai_"));
  }
  return false;
};

// Helper function to handle AI modal activation
const handleAiModalActivation = (
  aiActionsModal: ReturnType<typeof useAiActionsModal>,
  workflowId: string | undefined,
  triggeringWorkflowRef: React.MutableRefObject<string | null>,
) => {
  if (workflowId) {
    triggeringWorkflowRef.current = workflowId;
  }
  void aiActionsModal.show().then(() => {
    triggeringWorkflowRef.current = null;
  });
};

// Helper function to handle disabled agents modal
const handleDisabledAgentsModal = (
  disabledAgentsModal: ReturnType<typeof useDisabledAgentsModal>["modal"],
  effectiveActions: Action[],
  proceedWithActivation: () => void,
) => {
  void getAgentEnabledStatusInActions(effectiveActions).then(
    ({ disabledAgents }) => {
      if (disabledAgents.length > 0) {
        void disabledAgentsModal
          .show({
            disabledAgents,
            variant: "cancel",
          })
          .then(result => {
            if (result === MODAL_RESULT.SUCCESS) {
              void enableAgents(disabledAgents).then(() => {
                proceedWithActivation();
              });
            }
          });
      } else {
        proceedWithActivation();
      }
    },
  );
};

export const useAiWorkflowActivationModal = ({
  actions,
  steps,
  workflowId,
}: UseAiWorkflowActivationModalOptions = {}) => {
  const aiActionsModal = useAiActionsModal();
  const { modal: disabledAgentsModal } = useDisabledAgentsModal();
  const shouldShowAiModal = useShouldShowAiModal();

  const { data: workflowData } = useQuery({
    queryKey: ["workflow", workflowId],
    queryFn: () => workflowsApi.fetch({ id: workflowId ?? "" }),
    enabled: !actions && !!workflowId,
  });

  const effectiveActions = useMemo(
    () => actions ?? (workflowData?.actions as Action[] | undefined) ?? [],
    [actions, workflowData],
  );

  const hasWorkflowAiActions = hasAiActions(effectiveActions, steps);

  // Track which workflow triggered the modal
  const triggeringWorkflowRef = useRef<string | null>(null);

  const shouldBlockActivation = shouldShowAiModal && hasWorkflowAiActions;

  // Only consider this workflow affected by modal visibility if it's the one that triggered it
  const isModalVisibleForThisWorkflow =
    aiActionsModal.visible &&
    (workflowId ? triggeringWorkflowRef.current === workflowId : false);

  const handleActivationAttempt = useCallback(
    (proceedWithActivation: () => void) => {
      if (shouldBlockActivation) {
        handleAiModalActivation(
          aiActionsModal,
          workflowId,
          triggeringWorkflowRef,
        );
        return;
      }

      handleDisabledAgentsModal(
        disabledAgentsModal,
        effectiveActions,
        proceedWithActivation,
      );
    },
    [
      shouldBlockActivation,
      aiActionsModal,
      workflowId,
      disabledAgentsModal,
      effectiveActions,
    ],
  );

  return {
    shouldBlockActivation,
    handleActivationAttempt,
    showModal: useCallback(() => aiActionsModal.show(), [aiActionsModal]),
    isModalVisible: isModalVisibleForThisWorkflow,
  };
};
